import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useNavigate } from 'react-router-dom';
import { parsePhoneNumber } from 'awesome-phonenumber';

import { Regions, RegionsCode, WorldwideCode } from '../../../constants/region';
import { SVGIconType } from '../../../constants/svgIcon';
import {
  getWebPageCustomLink,
  WebPageType,
} from '../../../constants/webPageType';
import { TranslationLocale } from '../../../i18n';
import { Regions as TranslatedRegions } from '../../../models/SystemStore/SystemStore';
import { useSessionStore } from '../../../stores/SessionStore';
import { getRegionsList } from '../../../stores/SystemStore';
import { getLocaleText, getUiLocale } from '../../../utils/i18n';
import { getRegionInfo } from '../../../utils/region';
import { JoyajoyImage } from '../JoyajoyImage';
import { SvgIcon } from '../SVGIcon';

import styles from './ChangeCountry.module.css';

type ChangeCountryProps = {
  webPageType: WebPageType;
};

export const ChangeCountry = memo(({ webPageType }: ChangeCountryProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const activeRegion = useSessionStore((store) => store.activeRegion);

  const [translatedRegions, setTranslatedRegions] = useState<TranslatedRegions>(
    {},
  );

  const fetchTranslatedRegions = useCallback(async () => {
    const regions = await getRegionsList(
      getUiLocale(i18n.language as TranslationLocale),
    );
    setTranslatedRegions(regions);
  }, [i18n.language]);

  useEffect(() => {
    fetchTranslatedRegions().catch(console.error);
  }, [fetchTranslatedRegions]);

  const getRegionImage = (regionId: RegionsCode | typeof WorldwideCode) => {
    if (regionId === WorldwideCode) {
      return Regions.find((r) => r.id === WorldwideCode)?.image || '';
    }
    return getRegionInfo(regionId).image || '';
  };

  const isReplaceRoutes = [
    '/settings/account/region',
    '/settings/account/interface-language',
  ];

  const shouldReplaceRoute = isReplaceRoutes.some((route) =>
    matchPath(route, location.pathname),
  );

  const onClickRegion = useCallback(() => {
    useSessionStore
      .getState()
      .setScrollPos(window.scrollY || window.pageYOffset);
    navigate(getWebPageCustomLink(webPageType, '/settings/account/region'), {
      replace: shouldReplaceRoute,
    });
  }, [navigate, shouldReplaceRoute, webPageType]);

  const onClickLanguage = useCallback(() => {
    useSessionStore
      .getState()
      .setScrollPos(window.scrollY || window.pageYOffset);
    navigate(
      getWebPageCustomLink(webPageType, '/settings/account/interface-language'),
      { replace: shouldReplaceRoute },
    );
  }, [navigate, shouldReplaceRoute, webPageType]);

  const getServiceHotline = (code: RegionsCode): string => {
    switch (code) {
      case RegionsCode.HK:
        return '+852 28068380';
      case RegionsCode.CN:
        return '+86 15814710539';
      case RegionsCode.MO:
        return '+853 63502967';
      default:
        return '+852 28068380';
    }
  };

  const displayPhoneNumber = parsePhoneNumber(getServiceHotline(activeRegion), {
    regionCode: activeRegion ?? RegionsCode.HK,
  }).number?.international;

  return (
    <div className={styles.container}>
      <div className={styles.languageAndCountry}>
        <div className={styles.changeCountry} onClick={onClickLanguage}>
          <SvgIcon
            name={SVGIconType.HyperLink}
            className={styles.triangleIcon}
          />
          {t(getLocaleText(getUiLocale(i18n.language as TranslationLocale)))}
          <SvgIcon
            name={SVGIconType.Triangle}
            className={styles.triangleIcon}
          />
        </div>
        <div className={styles.changeCountry} onClick={onClickRegion}>
          <JoyajoyImage
            resource={{ uri: getRegionImage(activeRegion || WorldwideCode) }}
            style={{ width: 28, height: 28 }}
          />
          {translatedRegions[activeRegion || WorldwideCode]}
          <SvgIcon
            name={SVGIconType.Triangle}
            className={styles.triangleIcon}
          />
        </div>
      </div>
      <div className={styles.serviceHotline}>
        {t('Service Hotline: ')}
        <a
          href={`tel:${getServiceHotline(activeRegion)}`}
          className={styles.callLink}
        >
          {displayPhoneNumber}
        </a>
      </div>
    </div>
  );
});
